import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { State } from 'store'
import styled from 'styled-typed'
import { InlineDataTable, HeaderRow, DataRow, TableHeader, Cell, Sorting } from 'uiComponents/table'
import { ActionButton, ActionButtonA } from 'uiComponents/buttons'
import { UserListItem } from 'settings/schema'
import { History } from 'history'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencilAlt, faUserNinja } from '@fortawesome/pro-solid-svg-icons'
import { IconProp } from '@fortawesome/fontawesome'
import { startImpersonation } from 'auth/actions.async'
import { withFeatures } from 'features'
import { ChartDataLoader } from 'uiComponents/loaders'
import { MessageKind } from 'uiComponents/messages'
import { t } from 'translations'
import { iconActionColumnWidth } from 'uiComponents/table/actionColumnWidth'

const Own = styled.span`
    font-size: 0.7em;
    color: ${(props) => props.theme.colors.textLight};
    margin-left: 0.5em;
    font-style: italic;
`
const Icon = styled(FontAwesomeIcon)`
    font-size: 0.8em;
    margin-right: 0.7em;
    margin-bottom: 0.1em;
`

const getRoleLabel = (role: string) => {
    switch (role) {
        case 'admin':
            return t('settings.users.list.roles.admin')
        case 'user':
            return t('settings.users.list.roles.user')
        case 'staff':
            return t('settings.users.list.roles.staff')
        case 'reseller':
            return t('settings.users.list.roles.reseller')
        default:
            return role
    }
}

interface UserListTableProps {
    users: UserListItem[]
    ownEmail: string
    accountSlug: string
    history: History
    isConviousAdmin: boolean
    startImpersonation: typeof startImpersonation
    hasFeature: (feature: string, accountSlug: string) => boolean
    replaceMessages: (id: string, status: MessageKind, text: string) => void
}

interface UserListTableState {
    sortBy: string
    sortDirection: 'asc' | 'desc'
    users: UserListItem[]
    loading: boolean
}

class UserListTable extends React.Component<UserListTableProps, UserListTableState> {
    constructor(props: UserListTableProps) {
        super(props)
        this.state = {
            sortBy: 'role',
            sortDirection: 'asc',
            users: this.props.users,
            loading: false,
        }
    }

    componentDidMount() {
        this.onSortChanged({ prop: 'role', direction: 'asc' })
        let userList = [...this.state.users]
        const ownEntry = userList.filter((e) => e.email === this.props.ownEmail)
        if (ownEntry.length > 0) {
            userList = userList.filter((e) => e.email !== this.props.ownEmail)
            userList.unshift(ownEntry[0])
            this.setState({ users: userList })
        }
    }

    shouldComponentUpdate(nextProps: UserListTableProps, nextState: UserListTableState) {
        if (this.state.sortBy !== nextState.sortBy || this.state.sortDirection !== nextState.sortDirection) {
            return false
        }
        return true
    }

    sortByName = (user1: UserListItem, user2: UserListItem) => {
        const sortKeyA = `${user1['firstName']}${user1['lastName']}`.replace(/\s/g, '').toUpperCase()
        const sortKeyB = `${user2['firstName']}${user2['lastName']}`.replace(/\s/g, '').toUpperCase()
        if (sortKeyA < sortKeyB) {
            return -1
        }
        if (sortKeyA > sortKeyB) {
            return 1
        }
        return 0
    }

    sortByRoleOrEmail = (user1: UserListItem, user2: UserListItem) => {
        const sortKeyA = user1[this.state.sortBy].toUpperCase()
        const sortKeyB = user2[this.state.sortBy].toUpperCase()
        if (sortKeyA < sortKeyB) {
            return -1
        }
        if (sortKeyA > sortKeyB) {
            return 1
        }
        return 0
    }

    desc = (sortingFunc: (user1: UserListItem, user2: UserListItem) => {}) => {
        return (user1: UserListItem, user2: UserListItem) => -sortingFunc(user1, user2)
    }

    sortingStyle = {
        name: this.sortByName,
        role: this.sortByRoleOrEmail,
        email: this.sortByRoleOrEmail,
    }

    onSortChanged = (sorting: Sorting) => {
        this.setState(
            {
                sortBy: sorting.prop,
                sortDirection: sorting.direction,
            },
            () => {
                let sortByFunction = this.sortingStyle[sorting.prop]
                if (sorting.direction === 'desc') {
                    sortByFunction = this.desc(sortByFunction)
                }
                const sortedUsers = [...this.state.users].sort(sortByFunction)
                this.setState({
                    users: sortedUsers,
                })
            },
        )
    }

    table: HTMLDivElement | null = null

    setTableRef = (node: HTMLDivElement) => {
        this.table = node
    }

    onImpersonateUser = async (targetUser: string) => {
        try {
            this.setState({ loading: true })
            await this.props.startImpersonation(targetUser)
        } catch {
            this.props.replaceMessages('error', 'error', t('settings.users.list.impersonateError'))
        } finally {
            this.setState({ loading: false })
        }
    }

    render() {
        const sort: Sorting = {
            prop: this.state.sortBy,
            direction: this.state.sortDirection,
        }
        const impersonationAllowed = this.props.isConviousAdmin

        const editWidth = iconActionColumnWidth(t('common.edit'))
        const columnWidths = impersonationAllowed
            ? ['17em', null, '7em', iconActionColumnWidth(t('settings.users.list.impersonate')), editWidth]
            : ['20em', null, '10.3em', editWidth]

        return (
            <>
                {this.state.loading && <ChartDataLoader />}
                <InlineDataTable ref={this.setTableRef} id="users-list" columnWidths={columnWidths}>
                    <HeaderRow>
                        <TableHeader sortKey="name" sorting={sort} changeSort={this.onSortChanged}>
                            {t('settings.users.list.columns.name')}
                        </TableHeader>
                        <TableHeader sortKey="email" sorting={sort} changeSort={this.onSortChanged}>
                            {t('settings.users.list.columns.email')}
                        </TableHeader>
                        <TableHeader sortKey="role" sorting={sort} changeSort={this.onSortChanged}>
                            {t('settings.users.list.columns.role')}
                        </TableHeader>
                        {impersonationAllowed && <TableHeader nonInteractive />}
                        <TableHeader nonInteractive />
                    </HeaderRow>
                    {this.state.users.map((user) => (
                        <DataRow key={user.id} className="row" onClick={() => {}} narrow>
                            <Cell>
                                <span>
                                    {user.firstName} {user.lastName}
                                    {user.email === this.props.ownEmail && (
                                        <Own>{t('settings.users.list.ownUser')}</Own>
                                    )}
                                </span>
                            </Cell>
                            <Cell>{user.email}</Cell>
                            <Cell style={{ textTransform: 'capitalize' }} className="role">
                                {getRoleLabel(user.role)}
                            </Cell>
                            {impersonationAllowed && (
                                <Cell>
                                    <ActionButton
                                        kind="action"
                                        secondary
                                        className="impersonate-user"
                                        onClick={() => this.onImpersonateUser(user.email)}
                                    >
                                        <Icon icon={faUserNinja as IconProp} />
                                        {t('settings.users.list.impersonate')}
                                    </ActionButton>
                                </Cell>
                            )}
                            <Cell align="right">
                                {user.email !== this.props.ownEmail && (
                                    <ActionButtonA
                                        kind="action"
                                        secondary
                                        className="edit-user"
                                        href={`/account/${this.props.accountSlug}/settings/users/user_form/${user.id}`}
                                    >
                                        <Icon icon={faPencilAlt as IconProp} />
                                        {t('common.edit')}
                                    </ActionButtonA>
                                )}
                            </Cell>
                        </DataRow>
                    ))}
                </InlineDataTable>
            </>
        )
    }
}

function mapStateToProps(state: State) {
    return {
        isConviousAdmin: state.auth.user ? state.auth.user.isAdmin : false,
    }
}

function mapDispatchToProps(dispatch: Dispatch) {
    return bindActionCreators(
        {
            startImpersonation,
        },
        dispatch,
    )
}

export default withFeatures(connect(mapStateToProps, mapDispatchToProps)(UserListTable))
